'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import { PACKING_QUESTION_ID } from '../model/staticFlows';
import { StepFlow } from './StepFlow';

const QUESTION_ID = PACKING_QUESTION_ID;
const FLOW_ID = 'progress-packing';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /** 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치. */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 포장방식 step (Figma `225:14920`).
 *
 * single-select 3종 (포장/반포장/일반). 저장 시 `recordPacking` 호출.
 */
export function PackingStepAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-packing` 의 FlowDocument 를 비동기 로드.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);

  const initialValues = useMemo(() => {
    if (!counsel.packing?.method) return undefined;
    return { [QUESTION_ID]: counsel.packing.method };
  }, [counsel.packing?.method]);

  // 마운트 시점 intake-session 보장.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const method = values[QUESTION_ID];
      if (method !== 'FULL_PACKING' && method !== 'PARTIAL_PACKING' && method !== 'NO_PACKING') {
        showToast('포장방식을 선택해주세요.', 'warning');
        return;
      }
      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordPacking(visitCounselId, { method });
        showToast('포장방식이 저장되었습니다.', 'success');
        const result = await refetch();
        const updated = result?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '포장방식 저장에 실패했습니다.', 'error');
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  // packing 은 단일 질문이라 결과는 같지만 일관성을 위해 prop 전달.
  const startAtQuestionId = startAtLast ? QUESTION_ID : undefined;

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      startAtQuestionId={startAtQuestionId}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}
